<script setup lang="ts">
import { ref, reactive, onMounted, onBeforeUnmount, computed } from 'vue'
import QuantumFish from './QuantumFish.vue'
import QuantumFood from './QuantumFood.vue'
import { gsap } from 'gsap'

// 游戏状态
const gameState = reactive({
  started: false,
  chaosLevel: 0,
  score: 0,
  achievements: [] as string[],
  mutationCounter: 0,
})

// 鱼和食物管理
interface FishRef {
  id: number
  ref: InstanceType<typeof QuantumFish> | null
}

interface FoodRef {
  id: number
  x: number
  y: number
  ref: InstanceType<typeof QuantumFood> | null
}

const fishes = ref<FishRef[]>([])
const foods = ref<FoodRef[]>([])
const nextFishId = ref(1)
const nextFoodId = ref(1)
const feedingTimer = ref<number | null>(null)

// 鱼群生成
const generateInitialFish = () => {
  // 初始生成3条鱼
  for (let i = 0; i < 3; i++) {
    addFish()
  }
}

// 添加鱼
const addFish = () => {
  fishes.value.push({
    id: nextFishId.value,
    ref: null,
  })
  nextFishId.value++
}

// 添加食物
const addFood = (x: number, y: number) => {
  const id = nextFoodId.value
  foods.value.push({
    id,
    x,
    y,
    ref: null,
  })
  nextFoodId.value++

  return id
}

// 清理过期食物
const removeFood = (id: number) => {
  const index = foods.value.findIndex((food) => food.id === id)
  if (index !== -1) {
    foods.value.splice(index, 1)
  }
}

// 点击产生食物
const handleClick = (event: MouseEvent) => {
  if (!gameState.started) {
    startGame()
    return
  }

  // 创建量子饲料
  addFood(event.clientX, event.clientY)

  // 添加时空扭曲特效
  createSpaceTimeDistortion(event.clientX, event.clientY)

  // 检查混沌水平
  checkAndUpdateChaosLevel()
}

// 生成时空扭曲效果
const createSpaceTimeDistortion = (x: number, y: number) => {
  const distortion = document.createElement('div')
  distortion.className = 'space-time-distortion'
  distortion.style.left = `${x}px`
  distortion.style.top = `${y}px`
  document.body.appendChild(distortion)

  // 动画
  gsap.to(distortion, {
    width: '300px',
    height: '300px',
    opacity: 0,
    duration: 1,
    ease: 'power2.out',
    onComplete: () => {
      document.body.removeChild(distortion)
    },
  })
}

// 处理鱼的变异
const handleFishMutation = (fishId: number, mutationLevel: number) => {
  gameState.score += mutationLevel * 10
  gameState.mutationCounter += mutationLevel

  // 随机创建新鱼 (20%几率)
  if (mutationLevel > 2 && Math.random() > 0.8) {
    const fish = fishes.value.find((f) => f.id === fishId)
    if (fish && fish.ref) {
      // 从父鱼获取位置
      addFish()
    }
  }

  // 检查成就
  checkAchievements()
}

// 检查混沌水平
const checkAndUpdateChaosLevel = () => {
  // 每3次喂食增加混沌水平
  if (foods.value.length % 3 === 0) {
    gameState.chaosLevel += 1

    // 应用混沌效果
    if (gameState.chaosLevel >= 10) {
      triggerChaosEffect()
    }
  }
}

// 触发混沌效果
const triggerChaosEffect = () => {
  // 创建全屏特效
  const chaosOverlay = document.createElement('div')
  chaosOverlay.className = 'chaos-overlay'
  document.body.appendChild(chaosOverlay)

  // 扭曲动画
  gsap.to(chaosOverlay, {
    backgroundColor: 'rgba(255, 0, 255, 0.2)',
    backdropFilter: 'hue-rotate(90deg) blur(5px)',
    duration: 2,
    onComplete: () => {
      // 重置混沌水平
      gameState.chaosLevel = 0

      document.body.removeChild(chaosOverlay)
    },
  })

  // 随机添加新鱼
  addFish()
}

// 检查成就
const checkAchievements = () => {
  // 费米悖论：饲料未命中任何鱼体
  if (
    foods.value.length > 5 &&
    fishes.value.length === 3 &&
    !gameState.achievements.includes('费米悖论')
  ) {
    unlockAchievement('费米悖论')
  }

  // 量子纠缠：同时存在3个以上变异鱼
  if (gameState.mutationCounter >= 15 && !gameState.achievements.includes('量子纠缠')) {
    unlockAchievement('量子纠缠')
  }

  // 克苏鲁的午餐：高级变异体
  if (gameState.mutationCounter >= 30 && !gameState.achievements.includes('克苏鲁的午餐')) {
    unlockAchievement('克苏鲁的午餐')

    // 触发特殊视觉效果
    const root = document.documentElement
    root.style.setProperty('--background-distortion', '5px')
    setTimeout(() => {
      root.style.setProperty('--background-distortion', '0px')
    }, 5000)
  }
}

// 解锁成就
const unlockAchievement = (achievement: string) => {
  if (!gameState.achievements.includes(achievement)) {
    gameState.achievements.push(achievement)

    // 显示成就通知
    showAchievementNotification(achievement)
  }
}

// 显示成就通知
const showAchievementNotification = (achievement: string) => {
  const notification = document.createElement('div')
  notification.className = 'achievement-notification'
  notification.textContent = `成就解锁：${achievement}`
  document.body.appendChild(notification)

  // 动画
  gsap.fromTo(
    notification,
    { y: 50, opacity: 0 },
    {
      y: 0,
      opacity: 1,
      duration: 0.5,
      onComplete: () => {
        setTimeout(() => {
          gsap.to(notification, {
            y: -50,
            opacity: 0,
            duration: 0.5,
            onComplete: () => {
              document.body.removeChild(notification)
            },
          })
        }, 3000)
      },
    },
  )
}

// 检查食物与鱼的碰撞
const checkFoodFishCollisions = () => {
  foods.value.forEach((food) => {
    if (food.ref) {
      const foodPosition = food.ref.getPosition()

      // 检查所有鱼是否能吃到这个食物
      let eaten = false

      fishes.value.forEach((fish) => {
        if (fish.ref && !eaten) {
          // 检查这条鱼是否吃到食物
          if (fish.ref.checkFeedingHit(foodPosition.x, foodPosition.y)) {
            // 食物被吃掉
            if (food.ref) {
              food.ref.destroy()
            }
            eaten = true
          }
        }
      })
    }
  })

  // 每帧检查一次
  requestAnimationFrame(checkFoodFishCollisions)
}

// 开始游戏
const startGame = () => {
  if (gameState.started) return

  gameState.started = true
  generateInitialFish()

  // 启动碰撞检测
  checkFoodFishCollisions()
}

// 设置标题
const title = computed(() => {
  return gameState.started ? '量子饲料实验室' : '点击开始实验'
})

// 游戏说明
const instructions = [
  '点击屏幕生成量子饲料',
  '喂养量子鱼使其产生变异',
  '解锁特殊成就以发现隐藏效果',
]

// 生命周期钩子
onMounted(() => {
  document.addEventListener('click', handleClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClick)

  // 清理定时器
  if (feedingTimer.value) {
    clearInterval(feedingTimer.value)
  }
})
</script>

<template>
  <div class="quantum-aquarium" :class="{ started: gameState.started }">
    <!-- 游戏标题与说明 -->
    <div class="game-intro" v-if="!gameState.started">
      <h1>{{ title }}</h1>
      <div class="instructions">
        <div v-for="(instruction, index) in instructions" :key="index" class="instruction">
          {{ instruction }}
        </div>
      </div>
    </div>

    <!-- 游戏状态 -->
    <div class="game-status" v-if="gameState.started">
      <div class="score">分数：{{ gameState.score }}</div>
      <div class="chaos-meter">
        <div class="chaos-label">混沌量表</div>
        <div class="chaos-bar">
          <div class="chaos-fill" :style="{ width: `${gameState.chaosLevel * 10}%` }"></div>
        </div>
      </div>
    </div>

    <!-- 鱼群 -->
    <QuantumFish
      v-for="fish in fishes"
      :key="fish.id"
      :id="fish.id"
      ref="el => fish.ref = el"
      @mutate="handleFishMutation"
    />

    <!-- 饲料 -->
    <QuantumFood
      v-for="food in foods"
      :key="food.id"
      :id="food.id"
      :x="food.x"
      :y="food.y"
      ref="el => food.ref = el"
      @destroyed="removeFood"
    />

    <!-- 成就列表 -->
    <div class="achievements-panel" v-if="gameState.achievements.length > 0">
      <div class="achievements-title">成就</div>
      <div
        v-for="(achievement, index) in gameState.achievements"
        :key="index"
        class="achievement-item"
      >
        {{ achievement }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.quantum-aquarium {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, #0f0f3f 0%, #2a265f 50%, #4b2873 100%);
  cursor: pointer;
}

/* 时空扭曲动画 */
.space-time-distortion {
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0) 70%);
  transform: translate(-50%, -50%);
  z-index: 10;
  pointer-events: none;
}

/* 混沌叠加效果 */
.chaos-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(255, 0, 255, 0);
  z-index: 100;
  pointer-events: none;
}

/* 游戏介绍 */
.game-intro {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: white;
  z-index: 50;
}

.game-intro h1 {
  font-size: 3rem;
  font-weight: 300;
  margin-bottom: 2rem;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
  animation: pulse 2s infinite alternate;
}

.instructions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.instruction {
  font-size: 1.2rem;
  opacity: 0.8;
}

/* 游戏状态 */
.game-status {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 20;
  color: white;
  font-size: 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.score {
  padding: 5px 10px;
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 20px;
}

.chaos-meter {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.chaos-bar {
  width: 150px;
  height: 8px;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  overflow: hidden;
}

.chaos-fill {
  height: 100%;
  background: linear-gradient(90deg, #4f4fff, #ff4fff);
  transition: width 0.3s ease;
}

/* 成就面板 */
.achievements-panel {
  position: absolute;
  bottom: 20px;
  right: 20px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 10px;
  border-radius: 10px;
  z-index: 20;
  max-width: 300px;
}

.achievements-title {
  font-size: 1.2rem;
  margin-bottom: 10px;
  text-align: center;
}

.achievement-item {
  margin: 5px 0;
  padding: 5px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 5px;
}

/* 成就通知 */
.achievement-notification {
  position: fixed;
  top: 100px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(255, 215, 0, 0.9);
  color: #000;
  padding: 10px 20px;
  border-radius: 30px;
  font-weight: bold;
  box-shadow: 0 0 20px rgba(255, 215, 0, 0.5);
  z-index: 1000;
  pointer-events: none;
}

@keyframes pulse {
  0% {
    opacity: 0.8;
    text-shadow: 0 0 5px rgba(255, 255, 255, 0.5);
  }
  100% {
    opacity: 1;
    text-shadow: 0 0 20px rgba(255, 255, 255, 0.8);
  }
}
</style>
